import { subscribeSellerRealtime } from "@/shared/realtime";
import { Timestamp } from "firebase/firestore";
import {
  apiJsonClient,
} from "@/lib/query/core/api-json-client";
import {
  getPrivateSessionState,
  subscribePrivateSession,
} from "@/lib/api/private-session";
import type {
  AppNotification,
  NotificationFeedSnapshot,
} from "../types/notification-feed.types";

type FeedResponse = {
  items: Array<
    Omit<AppNotification, "createdAt"> & {
      createdAt: { seconds: number; nanoseconds: number } | null;
    }
  >;
  cursor: string;
};
/**
 * One bounded snapshot read. It never sends a cursor: that endpoint mode is a
 * long poll and would hold a Worker open.
 */
export async function readSellerNotificationFeed(
  signal: AbortSignal,
): Promise<NotificationFeedSnapshot> {
  const request = new AbortController();
  const cancel = () => request.abort();
  const deadline = setTimeout(cancel, 35_000);
  signal.addEventListener("abort", cancel, { once: true });
  if (signal.aborted) request.abort();
  try {
    const response = await apiJsonClient<FeedResponse>(
      "seller/notifications",
      { signal: request.signal, cache: "no-store" },
    );
    return {
      cursor: response.cursor,
      items: response.items.map((item) => ({
        ...item,
        createdAt: item.createdAt
          ? new Timestamp(item.createdAt.seconds, item.createdAt.nanoseconds)
          : null,
      })),
    };
  } finally {
    clearTimeout(deadline);
    signal.removeEventListener("abort", cancel);
  }
}

/**
 * One authenticated snapshot per revision/focus/online event. A burst during
 * an in-flight read is coalesced into one follow-up read.
 */
export function listenSellerNotificationFeed(
  onSnapshot: (snapshot: NotificationFeedSnapshot) => void,
  onError: (error: unknown) => void,
  generation = getPrivateSessionState().generation,
): () => void {
  const controller = new AbortController();
  let reading = false;
  let pending = false;
  const ownsSession = () => !controller.signal.aborted &&
    !getPrivateSessionState().changing && getPrivateSessionState().generation === generation;
  const refresh = () => {
    if (!ownsSession()) return;
    if (reading) { pending = true; return; }
    reading = true;
    void readSellerNotificationFeed(controller.signal).then(
      (snapshot) => { if (ownsSession()) onSnapshot(snapshot); },
      (error) => { if (ownsSession()) onError(error); },
    ).finally(() => {
      reading = false;
      if (pending && ownsSession()) { pending = false; refresh(); }
    });
  };
  const stopEvents = subscribeSellerRealtime((event) => {
    if (event.topics.includes("notifications")) refresh();
  });
  const stopSession = subscribePrivateSession(() => {
    if (!ownsSession()) controller.abort();
  });
  window.addEventListener("focus", refresh);
  window.addEventListener("online", refresh);
  // Subscribe before the baseline read so a revision during it queues one follow-up.
  refresh();
  return () => {
    controller.abort();
    stopEvents(); stopSession();
    window.removeEventListener("focus", refresh);
    window.removeEventListener("online", refresh);
  };
}
export function markSellerNotificationsRead(ids: string[]): Promise<void> {
  if (ids.length === 0) return Promise.resolve();
  return apiJsonClient("seller/notifications/read", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
}
